// ── Service data types ────────────────────────────────────────────────────────
// Used by the internal admin panel and the public repair intake form.
// Designed to map 1-to-1 with a future Supabase `services` table.
//
// Supabase migration hint (run once in dashboard):
//   CREATE TABLE services (
//     id               TEXT PRIMARY KEY,
//     sku              TEXT,
//     status           TEXT NOT NULL DEFAULT 'draft',
//     device_category  TEXT NOT NULL,
//     device_models    TEXT[],
//     repair_category  TEXT NOT NULL,
//     repair_type      TEXT NOT NULL,
//     description      TEXT,
//     base_price       NUMERIC,
//     rush_price       NUMERIC,
//     price_tiers      JSONB,
//     turnaround       TEXT,
//     rush_turnaround  TEXT,
//     warranty         TEXT,
//     mail_in_available BOOLEAN DEFAULT true,
//     internal_notes   TEXT,
//     disclaimer       TEXT,
//     tags             TEXT[],
//     image_url        TEXT DEFAULT '',
//     image_alt        TEXT DEFAULT '',
//     what_to_bring    TEXT DEFAULT '',
//     upsell_ids       TEXT[] DEFAULT '{}',
//     is_upsell        BOOLEAN DEFAULT false,
//     created_at       TIMESTAMPTZ DEFAULT now(),
//     updated_at       TIMESTAMPTZ DEFAULT now()
//   );

export type ServiceStatus = 'active' | 'draft' | 'hidden';

export type DeviceCategory =
  | 'ps5'
  | 'xbox'
  | 'switch'
  | 'nintendo-handheld'
  | 'handheld-pc'
  | 'controller'
  | 'laptop-macbook'
  | 'iphone'
  | 'ipad'
  | 'samsung'
  | 'other';

export type RepairCategory = 'repair' | 'upgrade' | 'mod' | 'custom';

export interface PriceTier {
  label: string;
  price: number | null; // null = TBD
  description?: string;
}

export interface ServiceEntry {
  id: string;
  sku: string;            // e.g. "PS5-HDMI", "SW-SCREEN-LCD"
  status: ServiceStatus;

  // ── Device ──────────────────────────────────────────────────────────────────
  deviceCategory: DeviceCategory;
  deviceModels: string[]; // empty array = applies to all models in category

  // ── Service ─────────────────────────────────────────────────────────────────
  repairCategory: RepairCategory;
  repairType: string;     // "HDMI Port Replacement"
  description: string;   // customer-facing

  // ── Pricing ─────────────────────────────────────────────────────────────────
  basePrice: number | null;     // null = TBD / quote required
  rushPrice: number | null;
  priceTiers: PriceTier[];      // for multi-tier services (e.g. stick drift)

  // ── Time ────────────────────────────────────────────────────────────────────
  turnaround: string;       // "3-4 hours"
  rushTurnaround: string;   // "1-2 hours"

  // ── Details ─────────────────────────────────────────────────────────────────
  warranty: string;
  mailInAvailable: boolean;

  // ── Internal only (never shown to customers) ─────────────────────────────────
  internalNotes: string;
  disclaimer: string;    // can be surfaced to customers
  tags: string[];

  // ── Media ───────────────────────────────────────────────────────────────────
  // Local images: save to /public/images/services/<name>.webp, use path /images/services/<name>.webp
  // Remote images: any full URL (https://...)
  imageUrl: string;   // '' = no image, show placeholder
  imageAlt: string;   // always set if imageUrl is set

  // ── Customer intake ──────────────────────────────────────────────────────────
  whatToBring: string;   // customer-facing "what to bring" instructions
  upsellIds: string[];   // service IDs to suggest as optional add-ons after this one is selected
  isUpsell: boolean;     // true = this service can appear in the add-ons step

  // ── Scheduling rules (source: Google Sheet columns) ──────────────────────────
  // requiresParentService: true = cannot be booked alone; must accompany a primary repair
  // eligibleParentServiceIds: which primary service IDs are valid parents for this add-on
  // serviceType: sheet-authoritative role (e.g. 'repair', 'add_on')
  requiresParentService?: boolean;
  eligibleParentServiceIds?: string[];
  serviceType?: string;

  // ── Meta ────────────────────────────────────────────────────────────────────
  createdAt: string;
  updatedAt: string;
}

export const DEVICE_CATEGORY_LABELS: Record<DeviceCategory, string> = {
  'ps5':              'PlayStation 5',
  'xbox':             'Xbox Series',
  'switch':           'Nintendo Switch',
  'nintendo-handheld':'Nintendo Handheld',
  'handheld-pc':      'Handheld Gaming PC',
  'controller':       'Controllers',
  'laptop-macbook':   'MacBook & Laptops',
  'iphone':           'iPhone',
  'ipad':             'iPad',
  'samsung':          'Samsung',
  'other':            'Other / Custom',
};

export const REPAIR_CATEGORY_LABELS: Record<RepairCategory, string> = {
  repair:  'Repair',
  upgrade: 'Upgrade',
  mod:     'Mod',
  custom:  'Custom',
};
